import once from "lodash/once";
import { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "@web-monorepo/i18next";
import AnimateUntilEnd from "components/AnimateUntilEnd";
import FullBleedScreen from "components/BeyondToolkit/FullBleedScreen";
import ToolkitAudioActivityChrome from "components/ToolkitAudioActivityChrome";
import { useAudioPlayer, playStates } from "hooks/useAudioPlayer";
import { openPointsSheet } from "src/pods/webviewInit";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { BRUSH_TEETH } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";

export default function BrushTeeth() {
  const brushTeeth = BRUSH_TEETH();
  // this tells i18next to load these namespaces
  const { ready } = useTranslation(["dojo.beyond"]);

  useEffect(() => {
    logParentToolkitEvent({ eventName: `${brushTeeth.loggingId}.loaded` });
  }, [brushTeeth.loggingId]);

  const sendUsageRef = useRef(once(() => sendUsage("brushTeeth")));

  const {
    play: playMain,
    pause,
    playState,
    secondsRemaining,
  } = useAudioPlayer({
    src: brushTeeth.mainAudio,
    expectedDuration: brushTeeth.duration,
  });

  const { play: playIntro, pause: pauseIntro } = useAudioPlayer({
    src: "/static/media/activity-brushteeth_start.mp3",
    expectedDuration: 17,
  });

  const { play: playOutro } = useAudioPlayer({
    src: "/static/media/activity-brushteeth_end.mp3",
    expectedDuration: 3,
  });

  // play intro audio clip on mount

  useEffect(() => {
    playIntro();
  }, [playIntro]);

  // pause intro clip when we start the main clip
  const play = useCallback(() => {
    pauseIntro();
    playMain();

    // send usage on first play event, and not on subsequent play events (i.e. after pause)
    sendUsageRef.current();
  }, [pauseIntro, playMain]);

  // play outro after the main clip

  useEffect(() => {
    if (playState === playStates.DONE) {
      playOutro();
      logParentToolkitEvent({ eventName: `${brushTeeth.loggingId}.activity_completed` });
    }
  }, [brushTeeth.loggingId, playOutro, playState]);

  const windowHeight = "100vh";

  if (!ready) return null;

  return (
    <FullBleedScreen background={brushTeeth.background}>
      <div sx={{ height: "100%", position: "relative" }}>
        <style>{`
        @keyframes brush {
          0% {
            transform: translate(10px, -10px);
          }

          50% {
            transform: translate(-10px, 10px);
          }

          100% {
            transform: translate(10px, -10px);
          }
        }

        @keyframes brushRotate {
          0% {
            transform: rotateZ(0) scale(1);
          }

          50% {
            transform: rotateZ(-10deg) scale(1.2);
          }

          100% {
            transform: rotateZ(0) scale(1);
          }
        }

        @keyframes bubbleRise {
          0% {
            transform: translateY(0);
          }

          100% {
            transform: translateY(calc(${windowHeight} * -0.6));
          }
        }

        @keyframes bubbleWave {
          0% {
            transform: translateX(-10px);
          }

          50% {
            transform: translateX(10px);
          }

          100% {
            transform: translateX(-10px);
          }
        }

        @keyframes bubbleScale {
          0% {
            transform: scale(0);
          }

          50% {
            transform: scale(1);
          }
        }
        `}</style>
        <ToolkitAudioActivityChrome
          loggingId={brushTeeth.loggingId}
          play={play}
          pause={pause}
          playState={playState}
          secondsRemaining={secondsRemaining}
          closeAndGivePoints={() => {
            openPointsSheet();
            window.history.back();
          }}
          mainContent={
            <AnimateUntilEnd
              shouldRun={playState === playStates.PLAYING}
              element={
                <div
                  sx={{
                    position: "absolute",
                    top: "calc(60% - 96px)",
                    left: "calc(50% - 96px)",
                    transformOrigin: "center",
                    animation: "brushRotate ease-in-out 3s",
                    animationFillMode: "both",
                    animationIterationCount: "infinite",
                  }}
                >
                  <AnimateUntilEnd
                    shouldRun={playState === playStates.PLAYING}
                    element={
                      <img
                        width="192px"
                        src={brushTeeth.imgUrl}
                        sx={{
                          animation: "brush ease-in-out .3s",
                          animationFillMode: "both",
                          animationIterationCount: "infinite",
                        }}
                      />
                    }
                  />
                </div>
              }
            />
          }
        />
        <BubbleRiseAnimation
          bubbleType="single"
          top="60%"
          left="calc(50% - 90px)"
          duration="3.5s"
          delay=".5s"
          shouldRun={playState === playStates.PLAYING}
        />
        <BubbleRiseAnimation
          bubbleType="compound"
          top="calc(60% - 20px)"
          left="calc(50% - 50px)"
          duration="3s"
          delay="0s"
          shouldRun={playState === playStates.PLAYING}
        />
        <BubbleRiseAnimation
          bubbleType="single"
          top="calc(60% - 60px)"
          left="calc(50% + 20px)"
          duration="2.5s"
          delay="1s"
          shouldRun={playState === playStates.PLAYING}
        />
        <BubbleRiseAnimation
          bubbleType="compound"
          top="calc(60% - 80px)"
          left="calc(50% + 60px)"
          duration="3s"
          delay="1.5s"
          shouldRun={playState === playStates.PLAYING}
        />
      </div>
    </FullBleedScreen>
  );
}

type BubbleRiseAnimationProps = {
  bubbleType: "single" | "compound";
  top: string;
  left: string;
  duration: string;
  delay: string;
  shouldRun: boolean;
};

function BubbleRiseAnimation({ bubbleType, top, left, duration, delay, shouldRun }: BubbleRiseAnimationProps) {
  return (
    <AnimateUntilEnd
      shouldRun={shouldRun}
      element={
        <div
          sx={{
            position: "absolute",
            top,
            left,
            animation: `bubbleRise ease-in ${duration}`,
            animationDelay: delay,
            animationFillMode: "both",
            animationIterationCount: "infinite",
          }}
        >
          <AnimateUntilEnd
            shouldRun={shouldRun}
            element={
              <div
                sx={{
                  animation: `bubbleScale ease-in ${duration}`,
                  animationDelay: delay,
                  animationFillMode: "both",
                  animationIterationCount: "infinite",
                }}
              >
                <img
                  src={`/static/images/${bubbleType === "compound" ? "compound" : "single"}_bubble.svg`}
                  sx={{
                    width: bubbleType === "compound" ? 67 : 48,
                    animation: "bubbleWave ease-in-out 1s",
                    animationFillMode: "both",
                    animationIterationCount: "infinite",
                    animationPlayState: "running",
                  }}
                />
              </div>
            }
          />
        </div>
      }
    />
  );
}
